import { UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { ImpersonationBroker, asApiError } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { FieldKey } from '../../viewModels/form';
import { IMeme } from '../../viewModels/gamification';
import { FollowUpOptionIds } from '../../viewModels/phonecall';
import { ILeadFollowUp, ISkipLeadRequest, LeadServedSource } from '../../viewModels/queue';
import { queryKeys } from './queryKeys';

export interface SkipLeadVariables {
	companyId: string;
	followUp: FollowUpOptionIds | ILeadFollowUp | null;
	/** Phone call id for the current call, if any. Pass `'manual'` for a non-dialer skip. */
	currentCallId?: string;
	/** Outcome / skip-reason id from `QueueViewModel.skipReason`. */
	outcomeId?: string;
	/** Friendly skip-reason name (`FieldKey`) — stored alongside the outcome. */
	skipReason?: FieldKey;
	/** Optional pre-saved note id tied to the skip. */
	noteId?: string;
	/** Source that served the lead (defaults to `Unspecified`). */
	leadServedSource?: LeadServedSource;
	/** Forward to downstream pools. Skips default to `false`. */
	doNotContact?: boolean;
}

/**
 * POSTs to `/lead/{companyId}/skip`, mirroring the payload shape of
 * `QueueViewModel.saveSkipReason` without importing the MobX viewmodel.
 *
 * The outcome/skip-reason/note are passed in as variables because in the new
 * Focused Lead View the UI owns selection state — no shared viewmodel holds
 * the "current" skip reason for us.
 *
 * On success the focused-view lead queries are invalidated so the next read
 * refetches. Returns the `meme` payload (when present) so the caller can
 * surface gamification through the focused-view `MemeOverlay` — mirrors the
 * legacy `QueueViewModel.saveSkipReason` → `gamification.setMeme(meme)` flow
 * (`viewModels/queue.ts:519-527`).
 */
export function useSkipLead(): UseMutationResult<IMeme | null, Error, SkipLeadVariables> {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation<IMeme | null, Error, SkipLeadVariables>({
		mutationFn: async variables => {
			const body: ISkipLeadRequest = {
				doNotContact: variables.doNotContact ?? false,
				leadServedSource: variables.leadServedSource ?? LeadServedSource.Unspecified,
				outcomeId: variables.outcomeId,
				phoneCallId: variables.currentCallId === 'manual' ? null : variables.currentCallId,
				skipReason: variables.skipReason,
				noteId: variables.noteId,
			};

			const { followUp } = variables;
			if (followUp && Object.values(FollowUpOptionIds).includes(followUp as FollowUpOptionIds)) {
				body.followUpOptionId = followUp as FollowUpOptionIds;
			} else if (followUp === null) {
				body.followUpOptionId = null;
			} else if (followUp && 'scheduled' in (followUp as ILeadFollowUp)) {
				const scheduled = followUp as ILeadFollowUp;
				body.followUpDate = scheduled.followUpDate;
				body.followUpImportanceLevel = scheduled.importance;
				body.followUpName = scheduled.name;
				body.scheduledFollowUp = scheduled.scheduled;
			}

			const result = await userSession.webServiceHelper.callWebServiceAsync<{ meme: IMeme }>(
				ImpersonationBroker.composeApiUrl({ urlPath: `lead/${variables.companyId}/skip` }),
				'POST',
				body
			);

			if (!result.success) {
				throw asApiError(result);
			}

			return result.value?.meme ?? null;
		},
		onSuccess: (_meme, variables) => {
			queryClient.invalidateQueries({ queryKey: queryKeys.focusedView.lead(variables.companyId) });
		},
	});
}
